// client/src/coms/personalisation/LayoutEditing.js
// On-screen layout editing (Settings → Layout → Customise on screen, or the topbar layout menu): the shell
// shows outlined regions with move/hide controls and highlighted resize handles; a floating bar offers
// presets, Apply, Cancel and Reset. Nothing is saved until Apply.
import React, { useEffect, useRef } from 'react';
import { usePersonalisation } from './PersonalisationProvider';
import { LAYOUT_PRESET_INFO } from './layoutModel';
import './personalisationSettings.css';

export function LayoutEditBar() {
  const pz = usePersonalisation();
  const applyRef = useRef(null);
  const finishRef = useRef(pz.finishLayoutEditing);
  finishRef.current = pz.finishLayoutEditing;
  useEffect(() => {
    if (!pz.layoutEditing) return undefined;
    document.documentElement.setAttribute('data-vx-layout-editing', '');
    const t = setTimeout(() => applyRef.current && applyRef.current.focus(), 0);
    const onKey = (e) => {
      if (e.key === 'Escape') finishRef.current(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.documentElement.removeAttribute('data-vx-layout-editing');
    };
  }, [pz.layoutEditing]);
  if (!pz.layoutEditing) return null;
  const layout = pz.layout;
  const shownPreset = pz.temp['layout.preset'] || layout.preset;
  const edit = pz.setEditValue;
  return (
    <div className="vx-layout-bar vx-pz" role="toolbar" aria-label="Customise layout" data-testid="layout-edit-bar">
      <strong>Customise layout</strong>
      <label className="vx-visually-hidden" htmlFor="vx-edit-preset">
        Preset
      </label>
      <select id="vx-edit-preset" className="vx-select" value={shownPreset} onChange={(e) => pz.setEditPreset(e.target.value)} data-testid="layout-edit-preset">
        {LAYOUT_PRESET_INFO.map((p) => (
          <option key={p.id} value={p.id} disabled={p.id === 'custom' && shownPreset !== 'custom'}>
            {p.label}
          </option>
        ))}
      </select>
      <button type="button" className="vx-btn small" onClick={() => edit('sidebar.visible', !layout.sidebar.visible)} aria-pressed={layout.sidebar.visible} data-testid="layout-edit-sidebar">
        {layout.sidebar.visible ? 'Hide channels' : 'Show channels'}
      </button>
      <button type="button" className="vx-btn small" onClick={() => edit('members.visible', !layout.members.visible)} aria-pressed={layout.members.visible} data-testid="layout-edit-members">
        {layout.members.visible ? 'Hide members' : 'Show members'}
      </button>
      <button type="button" className="vx-btn small" onClick={() => edit('chatDensity', layout.chatDensity === 'compact' ? 'cozy' : 'compact')} data-testid="layout-edit-density">
        {layout.chatDensity === 'compact' ? 'Cozy chat' : 'Compact chat'}
      </button>
      <button type="button" className="vx-btn small" onClick={pz.resetEdit} data-testid="layout-edit-reset">
        Reset
      </button>
      <button type="button" className="vx-btn small" onClick={() => pz.finishLayoutEditing(false)} data-testid="layout-edit-cancel">
        Cancel
      </button>
      <button ref={applyRef} type="button" className="vx-btn small primary" onClick={() => pz.finishLayoutEditing(true)} data-testid="layout-edit-apply">
        Apply
      </button>
    </div>
  );
}

// Label + quick actions shown on a region while editing.
export function RegionTag({ region }) {
  const pz = usePersonalisation();
  if (!pz.layoutEditing) return null;
  const layout = pz.layout;
  const edit = pz.setEditValue;
  if (region === 'sidebar') {
    return (
      <div className="vx-region-tag" data-testid="region-tag-sidebar">
        Channels
        <button type="button" onClick={() => edit('sidebar.side', layout.sidebar.side === 'left' ? 'right' : 'left')}>
          Move {layout.sidebar.side === 'left' ? 'right' : 'left'}
        </button>
        <button type="button" onClick={() => edit('sidebar.visible', false)}>
          Hide
        </button>
      </div>
    );
  }
  if (region === 'members') {
    return (
      <div className="vx-region-tag" data-testid="region-tag-members">
        Members
        <button type="button" onClick={() => edit('members.side', layout.members.side === 'right' ? 'left' : 'right')}>
          Move {layout.members.side === 'right' ? 'left' : 'right'}
        </button>
        <button type="button" onClick={() => edit('members.visible', false)}>
          Hide
        </button>
      </div>
    );
  }
  return (
    <div className="vx-region-tag" data-testid="region-tag-main">
      Conversation
      <button type="button" onClick={() => edit('userBar', layout.userBar === 'top' ? 'bottom' : 'top')}>
        Profile bar {layout.userBar === 'top' ? 'at bottom' : 'at top'}
      </button>
    </div>
  );
}

// Offered when a server suggests a theme the user has not answered yet. Never applies anything by itself.
export function ServerThemePrompt({ serverKey, serverName }) {
  const pz = usePersonalisation();
  const st = pz.serverTheme;
  const eff = pz.effective;
  if (!serverKey || !st || st.key !== serverKey || !st.theme) return null;
  if (pz.globalPrefs.theme.serverThemes === 'never') return null;
  if (eff.themeSuggestionSeen) return null;
  const previewing = pz.temp['theme.choice'] === 'server';
  if (eff.themeChoice !== 'global' && !previewing) return null;
  const answer = (choice) => {
    pz.setTempValue('theme.choice', undefined);
    if (choice) pz.setServerValue(serverKey, 'theme.choice', choice);
    pz.setServerValue(serverKey, 'theme.suggestionSeen', true);
  };
  return (
    <div className="vx-theme-prompt vx-pz" role="region" aria-label="Server theme suggestion" data-testid="server-theme-prompt">
      <span>
        {serverName || 'This server'} suggests the theme “{st.theme.name}”. {previewing ? 'You are previewing it.' : 'Your own theme stays unless you choose otherwise.'}
      </span>
      <button type="button" className="vx-btn small" onClick={() => pz.setTempValue('theme.choice', previewing ? undefined : 'server')} data-testid="server-theme-preview">
        {previewing ? 'Stop preview' : 'Preview'}
      </button>
      <button type="button" className="vx-btn small primary" onClick={() => answer('server')} data-testid="server-theme-use">
        Use in this server
      </button>
      <button type="button" className="vx-btn small" onClick={() => answer(null)} data-testid="server-theme-keep">
        Keep my theme
      </button>
    </div>
  );
}
